declare function createAPI(onLoad: (api: any) => void, parameters: object, views: object, elementID: string): any;

export enum GeogebraClientEvent {
  updateStyle = 'updateStyle',
  setMode = 'setMode',
  deselect = 'deselect',
  select = 'select',
  mouseDown = 'mouseDown',
  addPolygon = 'addPolygon',
  addPolygonComplete = 'addPolygonComplete',
  viewChanged2D = 'viewChanged2D',
  dragEnd = 'dragEnd',
  showStyleBar = 'showStyleBar',
  editorStart = 'editorStart',
  editorKeyTyped = 'editorKeyTyped',
  editorStop = 'editorStop',
  perspectiveChange = 'perspectiveChange',
  dropdownOpened = 'dropdownOpened',
  dropdownItemFocused = 'dropdownItemFocused',
  dropdownClosed = 'dropdownClosed'
}

type EventName = 'add' | 'delete' | 'update' | 'client';

declare function isServiceWorkerSupported(): boolean;
declare function installServiceWorker(): void;

declare function registerListener(eventName: EventName, api: any, callback: (label: string) => void): void;
declare function unregisterListener(eventName: EventName, api: any, callback: (label: string) => void): void;
declare function fixAndRemoveListeners(): void;
declare function recoverListeners(): void;

export interface ViewPort {
  xMin: number;
  yMin: number;
  xMax: number;
  yMax: number;
  width: number;
  height: number;
  invXscale: number;
  invYscale: number;
}

export class GeogebraApplet {
  private api: any;
  private readonly _applet: any;
  constructor(parameters: object, views: object, elementID: string, onLoad: () => void) {
    this._applet = createAPI((api: any): void => {
      this.api = api;
      onLoad();
    }, parameters, views, elementID);
  }

  public get applet(): any {
    return this._applet;
  }

  public static isServiceWorkerSupported(): boolean {
    return isServiceWorkerSupported();
  }
  public static installServiceWorker(): void {
    installServiceWorker();
  }

  public registerListener(eventName: EventName, callback: (label: string) => void): void {
    registerListener(eventName, this.api, callback);
  }

  public unregisterListener(eventName: EventName, callback: (label: string) => void): void {
    unregisterListener(eventName, this.api, callback);
  }

  public evalCommand(command: string): void {
    this.api.evalCommand(command);
  }

  public exportSVG(fileName: string): void {
    this.api.exportSVG(fileName);
  }

  public evalXML(xml: string): void {
    this.api.evalXML(xml);
    this.api.evalCommand('UpdateConstruction()');
  }

  public setCoordSystem(xMin: number, xMax: number, yMin: number, yMax: number): void {
    this.api.setCoordSystem(xMin, xMax, yMin, yMax);
  }

  public getViewProperties(): ViewPort {
    return JSON.parse(this.api.getViewProperties());
  }

  public getXMLByLabel(label: string): string {
    return this.api.getXML(label);
  }

  public getXML(): string{
    return this.api.getXML();
  }

  public setXML(xml: string): void {
    this.api.setXML(xml);
  }
  public getPerspectiveXML(): string {
    return this.api.getPerspectiveXML();
  }

  public setPerspective(perspective: string): void {
    return this.api.setPerspective(perspective);
  }

  public getCommandStringByLabel(label: string): string {
    return this.api.getCommandString(label);
  }

  public getAppletObject(appletId: any) {
    return this.api.getAppletObject(appletId);
  }

  public deleteObjectByLabel(label: string): void {
    this.api.deleteObject(label);
  }

  public runWithoutEventTriggering(callback: () => void): void {
    this.fixAndRemoveListeners();
    callback();
    this.recoverListeners();
  }

  private fixAndRemoveListeners(): void {
    fixAndRemoveListeners();
  }
  private recoverListeners(): void {
    recoverListeners();
  }

  public getInputBoxState(state: any): any {
    return this.api.getInputBoxState(state);
  }

  public setInputBoxState(state: any, event: any): any {
    this.api.setInputBoxState(state, event);
  }

  public getEditorState(): any {
    this.api.getEditorState();
  }
  public setEditorState(state: any): void {
    this.api.setEditorState(state);
  }

  public setWidth(width: number): void {
    this.api.setWidth(width);
  }
  public setHeight(height: number): void {
    this.api.setHeight(height);
  }
}
